import { useState, useEffect } from 'react';
import './styles.css';
import { AppProvider } from './AppContext';
import Dashboard from './pages/Dashboard';
import NewOrder from './pages/NewOrder';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Wallet from './pages/Wallet';
import PaymentReceipt from './pages/PaymentReceipt';
import { Ledger, Invoices, Reports } from './pages/OtherPages';
import Settings from './pages/Settings';
import BulkUpload from './pages/BulkUpload';
import ClientOnBoarding from './pages/ClientOnBoarding';
import { Login, RegisterCompany, ChangePasswordForcefully, ForgotPassword, ClientAuth, VerifyEmail } from './pages/Auth';
import { Register, DigiSign } from './pages/Register';
import SuperAdminConsole from './pages/SuperAdminConsole';
import AdminDashboard from './pages/AdminDashboard';
import AdminViewClients from './pages/AdminViewClients';
import AdminClientRegistrations from './pages/AdminClientRegistrations';
import AdminPaymentTransactions from './pages/AdminPaymentTransactions';

export default function App() {
  const [page, setPage] = useState(() => {
    const token = localStorage.getItem('token');
    if (!token) return 'login';
    // Reject expired JWTs before making any API call
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1]));
      if (exp && exp * 1000 <= Date.now()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('page');
        return 'login';
      }
    } catch {}
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if ((u.role || u.Role || '').toLowerCase() === 'superadmin') return 'admin-dashboard';
    } catch {}
    const saved = localStorage.getItem('page') || 'dashboard';
    if (saved === 'order-detail') return 'orders';
    if (saved === 'client-auth') return 'login';
    return saved;
  });
  const [pageParams, setPageParams] = useState({});

  useEffect(() => {
    const handler = () => {
      localStorage.removeItem('page');
      setPage('login');
      setPageParams({});
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, []);

  // Handle CCAvenue redirect back to app after payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ccaStatus = params.get('ccavenue');
    if (!ccaStatus) return;

    // Clean the query string from the URL without a page reload
    window.history.replaceState({}, '', window.location.pathname);

    const pending = JSON.parse(localStorage.getItem('ccavenue_pending') || '{}');
    localStorage.removeItem('ccavenue_pending');

    if (ccaStatus === 'success') {
      const orderId  = params.get('order_id') || pending.orderId || '';
      const tracking = params.get('tracking_id') || '';
      const bankRef  = params.get('bank_ref') || '';
      navigate('payment-receipt', {
        isSuccess: true,
        result: {
          transaction_id:      orderId,
          razorpay_payment_id: tracking || bankRef,
          topup_amount:        pending.topup_amount  || 0,
          cgst_amount:         pending.cgst_amount   || 0,
          sgst_amount:         pending.sgst_amount   || 0,
          igst_amount:         pending.igst_amount   || 0,
          payable_amount:      pending.payable_amount || 0,
          invoice_number:      '',
        },
        amounts: {
          topup_amount:  pending.topup_amount  || 0,
          cgst_amount:   pending.cgst_amount   || 0,
          sgst_amount:   pending.sgst_amount   || 0,
          igst_amount:   pending.igst_amount   || 0,
          payable_amount: pending.payable_amount || 0,
        },
        clientInfo: {
          clientName: pending.clientName || '',
          gstin:      pending.gstin      || '',
          email:      pending.email      || '',
        },
        currentBalance: pending.currentBalance || 0,
      });
    } else {
      const message = decodeURIComponent(params.get('message') || 'Payment failed or was cancelled.');
      navigate('payment-receipt', {
        isSuccess: false,
        error: { description: message },
        amounts: {
          topup_amount:  pending.topup_amount  || 0,
          cgst_amount:   pending.cgst_amount   || 0,
          sgst_amount:   pending.sgst_amount   || 0,
          igst_amount:   pending.igst_amount   || 0,
          payable_amount: pending.payable_amount || 0,
        },
        currentBalance: pending.currentBalance || 0,
      });
    }
  }, []);

  // Handle the self-signup email-verification link (?verify=<token>)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('verify');
    if (!token) return;

    // Clean the query string from the URL without a page reload
    window.history.replaceState({}, '', window.location.pathname);
    navigate('verify-email', { token });
  }, []);

  // Direct link to the public self-signup page (?register)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('register')) return;

    // Clean the query string from the URL without a page reload
    window.history.replaceState({}, '', window.location.pathname);
    navigate('register-company');
  }, []);

  function navigate(newPage, params = {}) {
    localStorage.setItem('page', newPage);
    setPage(newPage);
    setPageParams(params);
    window.scrollTo(0, 0);
  }

  const props = { onNavigate: navigate, ...pageParams };

  return (
    <AppProvider>
      {page === 'login' && <Login {...props} />}
      {page === 'register-company' && <RegisterCompany {...props} />}
      {page === 'register' && <Register {...props} />}
      {page === 'digisign' && <DigiSign {...props} />}
      {page === 'change-password' && <ChangePasswordForcefully {...props} />}
      {page === 'forgot-password' && <ForgotPassword {...props} />}
      {page === 'client-auth' && <ClientAuth {...props} />}
      {page === 'verify-email' && <VerifyEmail {...props} />}
      {page === 'dashboard' && <Dashboard {...props} />}
      {page === 'new-order' && <NewOrder {...props} initialTab="invite" />}
      {page === 'new-order-invite' && <NewOrder {...props} initialTab="invite" />}
      {page === 'new-order-direct' && <NewOrder {...props} initialTab="direct" />}
      {page === 'bulk-upload' && <BulkUpload {...props} />}
      {(page === 'orders' || page === 'order-detail') && <Orders {...props} />}
      {page === 'order-detail' && <OrderDetail {...props} />}
      {page === 'wallet' && <Wallet {...props} />}
      {page === 'payment-receipt' && <PaymentReceipt {...props} />}
      {page === 'ledger' && <Ledger {...props} />}
      {page === 'invoices' && <Invoices {...props} />}
      {page === 'reports' && <Reports {...props} />}
      {page === 'settings' && <Settings {...props} />}
      {page === 'client-onboarding' && <ClientOnBoarding {...props} />}
      {page === 'admin-dashboard' && <AdminDashboard {...props} />}
      {page === 'admin-view-clients' && <AdminViewClients {...props} />}
      {page === 'admin-client-registrations' && <AdminClientRegistrations {...props} />}
      {page === 'admin-payment-transactions' && <AdminPaymentTransactions {...props} />}
      {page === 'superadmin' && <SuperAdminConsole {...props} />}
    </AppProvider>
  );
}
